import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
// ✅ IMPORTING FROM ADMIN (Reuse Directory UI, Detail dashboard, and Firestore Fetch)
import StudentDirectory from "../../../admin/student/StudentDirectory";
import StudentDashboard from "../../../admin/student/dashboard/StudentDashboard";
import { TUTOR_PERMISSIONS } from "../../../admin/student/dashboard/permissions";
import {
  fetchStudents,
  saveStudent,
} from "../../../admin/student/studentFirestore";

// Fields the School tab can change — the only data a tutor is permitted to
// write. Everything else is read-only in the Tutor portal and blocked by rules.
const TUTOR_EDITABLE_FIELDS = [
  "schoolCoursesTable",
  "highSchoolCourses",
  "schoolGrades",
  "schoolGradeSnapshots",
  "schoolPlan",
  "schoolLogin",
  "schoolPortalUrl",
  "schoolLoginUsername",
  "schoolLoginPassword",
  "loginNote",
];

export default function TutorStudentManagement() {
  const location = useLocation();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Directory UI State
  const [directoryState, setDirectoryState] = useState({
    searchTerm: "",
    filterStatus: "All",
    sortKey: "firstAttendNewest",
  });

  // The detail view is driven by the URL (/students/directory/<studentId>) so it
  // supports direct navigation, browser back/forward, refresh persistence, and
  // shareable links — matching the Admin Portal. Browser navigation replaces the
  // old custom Back button.
  const routeState = useMemo(() => {
    const parts = location.pathname.replace(/\/+$/, "").split("/").filter(Boolean);
    const directoryIndex = parts.indexOf("directory");
    const studentId = directoryIndex >= 0 ? decodeURIComponent(parts[directoryIndex + 1] || "") : "";
    // Optional tab segment (e.g. /students/directory/<id>/plan) so links from the
    // Plans hub can open the profile on a specific section.
    const tab = directoryIndex >= 0 ? decodeURIComponent(parts[directoryIndex + 2] || "") : "";
    return { studentId, tab, isDetailRoute: !!studentId };
  }, [location.pathname]);

  const activeStudent = useMemo(() => {
    if (!routeState.isDetailRoute) return null;
    return (
      students.find((s) => s.id === routeState.studentId) ||
      (selectedStudent?.id === routeState.studentId ? selectedStudent : null)
    );
  }, [routeState.isDetailRoute, routeState.studentId, selectedStudent, students]);

  useEffect(() => {
    loadStudents();
  }, []);

  async function loadStudents() {
    setLoading(true);
    try {
      // Reusing the exact same fetch logic as Admin
      const data = await fetchStudents();
      setStudents(data);
    } catch (e) {
      console.error(e);
      alert("Could not load students.");
    } finally {
      setLoading(false);
    }
  }

  // Tutors cannot create new students.
  // We pass a dummy function or alert if they somehow trigger it.
  const handleCreateNew = () => {
    alert("Tutors cannot create new students. Please ask an Admin.");
  };

  function buildStudentPath(student) {
    return `/students/directory/${encodeURIComponent(student.id)}`;
  }

  const handleOpenStudent = (s, options = {}) => {
    setSelectedStudent(s);
    const nextPath = buildStudentPath(s);
    if (options?.newTab) {
      window.open(nextPath, "_blank", "noopener");
      return;
    }
    navigate(nextPath);
  };

  const handleUpdateStatus = (id, status) => {
    alert("Tutors cannot change student status.");
  };

  // Mirrors the admin save signature so the shared dashboard's debounced
  // autosave (School tab) works. Tutor edits are restricted at the UI level by
  // TUTOR_PERMISSIONS (only the School tab is editable). We additionally narrow
  // the write to School-related fields so the payload can never touch the
  // admin-only fields that Firestore rules forbid tutors from changing
  // (name/grade/school/dob/startDate/parent fields/customFields).
  const handleSaveStudent = async (studentId, changes, options = {}) => {
    const allowed = {};
    for (const key of TUTOR_EDITABLE_FIELDS) {
      if (changes && Object.prototype.hasOwnProperty.call(changes, key)) {
        allowed[key] = changes[key];
      }
    }
    if (Object.keys(allowed).length === 0) return; // nothing a tutor may write

    try {
      await saveStudent(studentId, allowed);

      // Update local state
      setStudents((prev) => prev.map((s) => (s.id === studentId ? { ...s, ...allowed } : s)));
      setSelectedStudent((prev) => (prev?.id === studentId ? { ...prev, ...allowed } : prev));
      if (!options?.silent) alert("Saved.");
    } catch (e) {
      console.error(e);
      if (!options?.silent) alert("Error saving: " + e.message);
      throw e;
    }
  };

  // On a detail URL: render the dashboard once the student record is available.
  // (activeStudent resolves from the loaded list or the just-clicked selection.)
  // No onBack is passed — browser back/forward handles navigation, so the shared
  // dashboard header omits its custom Back button.
  if (routeState.isDetailRoute && activeStudent) {
    return (
      <StudentDashboard
        student={activeStudent}
        permissions={TUTOR_PERMISSIONS}
        activeTabKey={routeState.tab || undefined}
        onSaveStudent={handleSaveStudent}
      />
    );
  }

  return (
    <StudentDirectory
      students={students}
      loading={loading}
      onRefresh={loadStudents}
      onCreateNew={handleCreateNew} // Handled with alert
      onOpenStudent={handleOpenStudent}
      onUpdateStatus={handleUpdateStatus} // Handled with alert
      allowCreate={false}
      allowStatusEdit={false}
      allowConferenceSkip={false}
      getStudentHref={(student) => buildStudentPath(student)}
      // UI State
      searchTerm={directoryState.searchTerm}
      filterStatus={directoryState.filterStatus}
      sortKey={directoryState.sortKey}
      onDirectoryStateChange={(patch) => setDirectoryState(p => ({ ...p, ...patch }))}
    />
  );
}
